/**
 * User Filter Options Form Component
 *
 * Reusable form for filtering valid users in batch execution.
 * Used by both workflow execution dialogs and schedule creation.
 * All filters are combined with AND logic.
 */

import { useCallback } from "react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import type { UserFilterOptions } from "@/types/imageWorkflow";

export interface UserFilterOptionsFormProps {
  value: UserFilterOptions;
  onChange: (value: UserFilterOptions) => void;
  disabled?: boolean;
}

const ROLE_LEVEL_OPTIONS = [
  { value: "", label: "不限制" },
  { value: "free", label: "Free 及以上" },
  { value: "premium", label: "Premium 及以上" },
  { value: "vip", label: "VIP 及以上" },
  { value: "pro", label: "Pro" },
];

export function UserFilterOptionsForm({
  value,
  onChange,
  disabled = false,
}: UserFilterOptionsFormProps) {
  const handleExcludeTestChange = useCallback(
    (checked: boolean) => {
      onChange({ ...value, exclude_test: checked });
    },
    [value, onChange]
  );

  const handleMinRoleLevelChange = useCallback(
    (roleLevel: string) => {
      onChange({
        ...value,
        min_role_level: (roleLevel || undefined) as
          | "free"
          | "premium"
          | "vip"
          | "pro"
          | undefined,
      });
    },
    [value, onChange]
  );

  const handleActiveDaysChange = useCallback(
    (days: string) => {
      const parsed = parseInt(days, 10);
      onChange({
        ...value,
        active_days: parsed > 0 ? parsed : undefined,
      });
    },
    [value, onChange]
  );

  const handleHasSelfBirthChartChange = useCallback(
    (checked: boolean) => {
      onChange({ ...value, has_self_birth_chart: checked });
    },
    [value, onChange]
  );

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">用户过滤条件</h4>
        <span className="text-xs text-gray-500">多个条件为 AND 关系</span>
      </div>

      {/* Filter Options */}
      <div className="space-y-3">
        {/* 1. Exclude Test Users */}
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={value.exclude_test ?? true}
            onChange={(e) => handleExcludeTestChange(e.target.checked)}
            disabled={disabled}
            className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">
              排除测试用户
            </div>
            <div className="text-xs text-gray-500">
              过滤掉 user_id 以 "test_" 开头的用户
            </div>
          </div>
        </label>

        {/* 2. Minimum Role Level */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            最低角色等级
          </label>
          <Select
            value={value.min_role_level || ""}
            onChange={(e) => handleMinRoleLevelChange(e.target.value)}
            disabled={disabled}
            options={ROLE_LEVEL_OPTIONS}
          />
          <div className="text-xs text-gray-500">
            只包含指定等级及以上的用户（free &lt; premium &lt; vip &lt; pro）
          </div>
        </div>

        {/* 3. Active Days */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            最近活跃天数
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              max="365"
              value={value.active_days || ""}
              onChange={(e) => handleActiveDaysChange(e.target.value)}
              placeholder="不限制"
              disabled={disabled}
              className="w-32"
            />
            <span className="text-sm text-gray-500">天内活跃的用户</span>
          </div>
          <div className="text-xs text-gray-500">
            基于 updated_at 字段筛选最近 N 天内有活动的用户
          </div>
        </div>

        {/* 4. Has Self Birth Chart */}
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={value.has_self_birth_chart ?? false}
            onChange={(e) => handleHasSelfBirthChartChange(e.target.checked)}
            disabled={disabled}
            className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">
              仅有 self 星盘的用户
            </div>
            <div className="text-xs text-gray-500">
              只包含在 user_birth_charts 表中有 is_self=true 记录的用户
            </div>
          </div>
        </label>
      </div>

      {/* Filter Summary */}
      {(value.exclude_test ||
        value.min_role_level ||
        value.active_days ||
        value.has_self_birth_chart) && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="text-xs font-medium text-blue-900 mb-1">
            当前过滤条件：
          </div>
          <ul className="text-xs text-blue-800 space-y-0.5">
            {value.exclude_test && <li>• 排除测试用户</li>}
            {value.min_role_level && (
              <li>• 角色等级 ≥ {value.min_role_level}</li>
            )}
            {value.active_days && <li>• {value.active_days} 天内活跃</li>}
            {value.has_self_birth_chart && <li>• 有 self 星盘</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
